import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, DollarSign, RefreshCw, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getStripeEnvironment } from "@/lib/stripe";

type Identity = { email: string | null; display_name: string | null; workspace_label: string | null };
type Overview = {
  environment: "sandbox" | "live";
  mrr_estimate_cents: number;
  counts_by_status: Record<string, number>;
  counts_by_price: Record<string, number>;
  price_meta: Record<string, { unit_amount: number | null; currency: string; interval: string | null; product_name: string | null }>;
  canceled_this_month: number;
  pending_downgrades: Array<{ user_id: string; pending_tier: string; pending_effective_at: string | null; current_price_id: string }>;
  at_risk: Array<{ user_id: string; status: string; price_id: string; current_period_end: string | null; cancel_at_period_end: boolean }>;
  recent_changes: Array<{ user_id: string; stripe_subscription_id: string; price_id: string; status: string; cancel_at_period_end: boolean; updated_at: string }>;
  identities: Record<string, Identity>;
};

const fmtMoney = (cents: number, currency = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);

const labelFor = (id: string, identities: Record<string, Identity>) => {
  const i = identities[id];
  return i?.workspace_label || i?.display_name || i?.email || id.slice(0, 8);
};

const statusVariant = (s: string) => {
  if (s === "active" || s === "trialing") return "default" as const;
  if (s === "past_due" || s === "unpaid" || s === "incomplete") return "destructive" as const;
  return "secondary" as const;
};

export const BillingTab = () => {
  const navigate = useNavigate();
  const [env, setEnv] = useState<"sandbox" | "live">(getStripeEnvironment());
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async (which: "sandbox" | "live") => {
    setLoading(true);
    const { data: payload, error } = await supabase.functions.invoke("admin-billing-overview", {
      body: { environment: which },
    });
    setLoading(false);
    if (error) { toast.error(`Could not load billing overview: ${error.message}`); return; }
    setData(payload as Overview);
  };

  useEffect(() => { void load(env); }, [env]);

  const goToUser = (userId: string) => navigate(`/admin?tab=users&user=${userId}`);

  const planName = (priceId: string) =>
    data?.price_meta[priceId]?.product_name ?? priceId;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-lg font-medium">Billing overview</h2>
          <p className="text-xs text-muted-foreground">Snapshot. MRR is estimated from current active + trialing subscriptions.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={env} onValueChange={(v) => setEnv(v as "sandbox" | "live")}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sandbox">Test</SelectItem>
              <SelectItem value="live">Live</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => load(env)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {loading && !data && <Skeleton className="h-32 w-full" />}

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard icon={<DollarSign className="h-4 w-4" />} label="MRR (est.)" value={fmtMoney(data.mrr_estimate_cents)} />
            <StatCard label="Active" value={String(data.counts_by_status.active ?? 0)} />
            <StatCard label="Trialing" value={String(data.counts_by_status.trialing ?? 0)} />
            <StatCard icon={<AlertTriangle className="h-4 w-4 text-destructive" />} label="Past due" value={String(data.counts_by_status.past_due ?? 0)} />
            <StatCard icon={<TrendingDown className="h-4 w-4" />} label="Canceled this month" value={String(data.canceled_this_month)} />
          </div>

          <Card className="shadow-soft">
            <CardHeader><CardTitle className="text-base">Plan distribution (active + trialing)</CardTitle></CardHeader>
            <CardContent>
              {Object.keys(data.counts_by_price).length === 0 ? (
                <p className="text-sm text-muted-foreground">No active subscriptions in {env}.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {Object.entries(data.counts_by_price)
                    .sort((a, b) => b[1] - a[1])
                    .map(([priceId, count]) => {
                      const meta = data.price_meta[priceId];
                      return (
                        <li key={priceId} className="flex items-center justify-between gap-2">
                          <span className="truncate">
                            {planName(priceId)}
                            {meta?.unit_amount != null && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {fmtMoney(meta.unit_amount, meta.currency)}/{meta.interval ?? ""}
                              </span>
                            )}
                          </span>
                          <Badge variant="secondary">{count}</Badge>
                        </li>
                      );
                    })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-soft">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-destructive" /> At risk</CardTitle></CardHeader>
            <CardContent>
              {data.at_risk.length === 0 ? (
                <p className="text-sm text-muted-foreground">No past-due or grace-period accounts. 🎉</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.at_risk.map((r) => (
                    <li key={r.user_id} className="flex items-center justify-between gap-2">
                      <button className="truncate text-left hover:underline" onClick={() => goToUser(r.user_id)}>
                        {labelFor(r.user_id, data.identities)}
                      </button>
                      <span className="flex items-center gap-2">
                        <Badge variant={statusVariant(r.status)} className="text-[10px]">{r.status}</Badge>
                        <span className="text-xs text-muted-foreground">{planName(r.price_id)}</span>
                        {r.current_period_end && (
                          <span className="text-xs text-muted-foreground">until {new Date(r.current_period_end).toLocaleDateString()}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-soft">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Pending downgrades</CardTitle></CardHeader>
            <CardContent>
              {data.pending_downgrades.length === 0 ? (
                <p className="text-sm text-muted-foreground">No scheduled downgrades.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.pending_downgrades.map((p) => (
                    <li key={p.user_id} className="flex items-center justify-between gap-2">
                      <button className="truncate text-left hover:underline" onClick={() => goToUser(p.user_id)}>
                        {labelFor(p.user_id, data.identities)}
                      </button>
                      <span className="text-xs text-muted-foreground">
                        → {p.pending_tier}
                        {p.pending_effective_at && ` on ${new Date(p.pending_effective_at).toLocaleDateString()}`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-soft">
            <CardHeader><CardTitle className="text-base">Recent subscription changes</CardTitle></CardHeader>
            <CardContent>
              {data.recent_changes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity in {env} yet.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.recent_changes.map((c) => (
                    <li key={c.stripe_subscription_id} className="flex items-center justify-between gap-2">
                      <button className="truncate text-left hover:underline" onClick={() => goToUser(c.user_id)}>
                        {labelFor(c.user_id, data.identities)}
                      </button>
                      <span className="flex items-center gap-2">
                        <Badge variant={statusVariant(c.status)} className="text-[10px]">{c.status}</Badge>
                        <span className="text-xs text-muted-foreground">{planName(c.price_id)}</span>
                        <span className="text-xs text-muted-foreground">{new Date(c.updated_at).toLocaleString()}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const StatCard = ({ icon, label, value }: { icon?: React.ReactNode; label: string; value: string }) => (
  <Card className="shadow-soft">
    <CardContent className="space-y-1 p-4">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon} {label}
      </div>
      <div className="text-2xl font-semibold">{value}</div>
    </CardContent>
  </Card>
);
